import React from 'react';
import { Box, Typography } from '@mui/material';
import { Doughnut } from 'react-chartjs-2';
import { TooltipItem } from 'chart.js';

import { PlacementBucket } from '../data/player_insights';
import { DS, numericSx } from '../shared/Dashboard';

export const PLACE_META: Record<number, { label: string; color: string }> = {
    1: { label: '1st', color: DS.gold },
    2: { label: '2nd', color: DS.silver },
    3: { label: '3rd', color: DS.bronze },
    4: { label: '4th', color: DS.textMuted },
    0: { label: 'DNF / Retired', color: DS.loss },
};

/**
 * Compact placement doughnut with its legend alongside, so the counts are
 * readable without hovering.
 */
const PlacementDoughnut: React.FC<{ buckets: PlacementBucket[]; size?: number }> = ({ buckets, size = 120 }) => {
    const total = buckets.reduce((acc, b) => acc + b.count, 0);

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
            <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
                <Doughnut
                    data={{
                        labels: buckets.map(b => PLACE_META[b.place].label),
                        datasets: [{
                            data: buckets.map(b => b.count),
                            backgroundColor: buckets.map(b => PLACE_META[b.place].color),
                            borderColor: DS.surface,
                            borderWidth: 2,
                        }],
                    }}
                    options={{
                        cutout: '64%',
                        maintainAspectRatio: false,
                        animation: { duration: 500 },
                        plugins: {
                            legend: { display: false },
                            datalabels: { display: false },
                            tooltip: {
                                callbacks: {
                                    label: (ctx: TooltipItem<'doughnut'>) => ` ${ctx.parsed} round${ctx.parsed !== 1 ? 's' : ''} · ${buckets[ctx.dataIndex].percent}%`,
                                },
                            },
                        } as any,
                    }}
                />
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                    <Typography sx={{ fontSize: '20px', fontWeight: 800, color: DS.text, lineHeight: 1, ...numericSx }}>{total}</Typography>
                    <Typography sx={{ fontSize: '11px', color: DS.textMuted }}>rounds</Typography>
                </Box>
            </Box>

            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {buckets.map(b => {
                    const { label, color } = PLACE_META[b.place];
                    return (
                        <Box key={b.place} sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: b.count ? 1 : 0.45 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '3px', background: color, flexShrink: 0 }} />
                            <Typography sx={{ flex: 1, fontSize: '13px', color: DS.textMuted, whiteSpace: 'nowrap' }}>{label}</Typography>
                            <Typography sx={{ fontSize: '13px', color: DS.textMuted, ...numericSx }}>
                                <strong style={{ color: DS.text }}>{b.count}</strong> · {b.percent}%
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

export default PlacementDoughnut;
